import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { useAssistant } from "../../config/assistantProfile.ts";
import { getConversationLogs, getPromptProgress, submitPrompt, type ConversationResponse, type LogResponse } from "./issaInsightsApi.ts";

export type IssaPromptStatus = "pending" | "completed" | "error" | "cancelled";
export type IssaPromptOrigin = "page" | "quick-chat";

export interface IssaPromptTask {
  id: string;
  conversationGuid: string;
  prompt: string;
  displayPrompt?: string;
  model: string;
  endpointId: number;
  origin: IssaPromptOrigin;
  startedAt: number;
  conversationWasEmpty: boolean;
  status: IssaPromptStatus;
  statusText?: string;
  /** The final answer streamed so far, shown while the task is pending. */
  partialText?: string;
  detached: boolean;
  notifyWhenReady: boolean;
  unread: boolean;
  result?: ConversationResponse;
  logs?: LogResponse[];
  error?: string;
}

interface StartPromptInput {
  conversationGuid: string;
  prompt: string;
  displayPrompt?: string;
  model: string;
  origin: IssaPromptOrigin;
  conversationWasEmpty?: boolean;
}

interface IssaPromptContextValue {
  tasks: IssaPromptTask[];
  unreadTasks: IssaPromptTask[];
  startPrompt: (input: StartPromptInput) => string;
  cancelPrompt: (id: string) => void;
  notifyWhenReady: (id: string) => void;
  removeTask: (id: string) => void;
  markRead: (id: string) => void;
}

const IssaPromptContext = createContext<IssaPromptContextValue | null>(null);
const SLOW_PROMPT_MS = 30_000;
// The server gives InstantAI 8 minutes (IssaInsights.vb IssaRequestTimeout). Waiting half a
// minute longer lets its timeout error arrive; an abort here would show as "cancelled".
const REQUEST_TIMEOUT_MS = 8 * 60_000 + 30_000;

export function IssaPromptProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const { toast, dismissToast } = useToast();
  const assistant = useAssistant();
  const [tasks, setTasks] = useState<IssaPromptTask[]>([]);
  const controllersRef = useRef(new Map<string, AbortController>());
  const slowTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const timeoutTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const pollTimersRef = useRef(new Map<string, ReturnType<typeof setInterval>>());
  const slowToastIdsRef = useRef(new Map<string, number>());

  const patchTask = useCallback((id: string, patch: Partial<IssaPromptTask>) => {
    setTasks((current) => current.map((task) => task.id === id ? { ...task, ...patch } : task));
  }, []);

  const clearTaskTimers = useCallback((id: string) => {
    const slow = slowTimersRef.current.get(id);
    if (slow) clearTimeout(slow);
    slowTimersRef.current.delete(id);
    const timeout = timeoutTimersRef.current.get(id);
    if (timeout) clearTimeout(timeout);
    timeoutTimersRef.current.delete(id);
    const poll = pollTimersRef.current.get(id);
    if (poll) clearInterval(poll);
    pollTimersRef.current.delete(id);
    const toastId = slowToastIdsRef.current.get(id);
    if (toastId != null) dismissToast(toastId);
    slowToastIdsRef.current.delete(id);
  }, [dismissToast]);

  const clearSlowOffer = useCallback((id: string) => {
    const slow = slowTimersRef.current.get(id);
    if (slow) clearTimeout(slow);
    slowTimersRef.current.delete(id);
    const toastId = slowToastIdsRef.current.get(id);
    if (toastId != null) dismissToast(toastId);
    slowToastIdsRef.current.delete(id);
  }, [dismissToast]);

  const notifyWhenReady = useCallback((id: string) => {
    clearSlowOffer(id);
    setTasks((current) => current.map((task) => task.id === id
      ? {
          ...task,
          detached: true,
          notifyWhenReady: true,
          unread: task.status === "completed" || task.unread,
        }
      : task));
  }, [clearSlowOffer]);

  const cancelPrompt = useCallback((id: string) => {
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
    clearTaskTimers(id);
    patchTask(id, { status: "cancelled" });
  }, [clearTaskTimers, patchTask]);

  const startPrompt = useCallback((input: StartPromptInput) => {
    const id = crypto.randomUUID();
    const controller = new AbortController();
    const task: IssaPromptTask = {
      id,
      conversationGuid: input.conversationGuid,
      prompt: input.prompt,
      displayPrompt: input.displayPrompt,
      model: input.model,
      endpointId: session?.ActiveEndPointID ?? 0,
      origin: input.origin,
      startedAt: Date.now(),
      conversationWasEmpty: input.conversationWasEmpty ?? true,
      status: "pending",
      detached: false,
      notifyWhenReady: false,
      unread: false,
    };
    controllersRef.current.set(id, controller);
    setTasks((current) => [...current, task]);

    slowTimersRef.current.set(id, setTimeout(() => {
      const toastId = toast(`${assistant.name} is still working. Notify you when the answer is ready?`, "info", {
        persistent: true,
        actions: [
          { label: "Keep waiting", onClick: () => {} },
          { label: "Notify me", primary: true, onClick: () => notifyWhenReady(id) },
        ],
      });
      slowToastIdsRef.current.set(id, toastId);
    }, SLOW_PROMPT_MS));

    timeoutTimersRef.current.set(id, setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS));
    pollTimersRef.current.set(id, setInterval(async () => {
      try {
        const progress = await getPromptProgress(input.conversationGuid);
        if (!controller.signal.aborted && progress) {
          patchTask(id, {
            ...(progress.Status ? { statusText: progress.Status } : {}),
            // Empty string means InstantAI cleared a partial that turned out
            // to be a tool call - the chat must drop it too.
            partialText: progress.PartialAnswer || undefined,
          });
        }
      } catch { /* progress is best-effort */ }
    }, 1200));

    void submitPrompt(input.prompt, input.conversationGuid, input.model, controller.signal, input.displayPrompt)
      .then(async (result) => {
        if (controller.signal.aborted) return;
        const logs = await getConversationLogs(result.ConversationGUID);
        setTasks((current) => current.map((currentTask) => currentTask.id === id
          ? {
              ...currentTask,
              conversationGuid: result.ConversationGUID,
              status: "completed",
              statusText: undefined,
              partialText: undefined,
              result,
              logs,
              unread: currentTask.notifyWhenReady,
            }
          : currentTask));
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          patchTask(id, { status: "cancelled", statusText: undefined, partialText: undefined });
          return;
        }
        patchTask(id, {
          status: "error",
          statusText: undefined,
          partialText: undefined,
          error: error instanceof Error ? error.message : "Failed to get response",
        });
      })
      .finally(() => {
        controllersRef.current.delete(id);
        clearTaskTimers(id);
      });

    return id;
  }, [assistant.name, clearTaskTimers, notifyWhenReady, patchTask, session?.ActiveEndPointID, toast]);

  const markRead = useCallback((id: string) => patchTask(id, { unread: false }), [patchTask]);
  const removeTask = useCallback((id: string) => {
    clearTaskTimers(id);
    controllersRef.current.get(id)?.abort();
    controllersRef.current.delete(id);
    setTasks((current) => current.filter((task) => task.id !== id));
  }, [clearTaskTimers]);

  const sessionKey = `${session?.UID ?? 0}|${session?.ActiveEndPointID ?? 0}`;
  const previousSessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    if (previousSessionKeyRef.current === sessionKey) return;
    previousSessionKeyRef.current = sessionKey;
    for (const controller of controllersRef.current.values()) controller.abort();
    for (const id of controllersRef.current.keys()) clearTaskTimers(id);
    controllersRef.current.clear();
    setTasks([]);
  }, [clearTaskTimers, sessionKey]);

  useEffect(() => () => {
    for (const controller of controllersRef.current.values()) controller.abort();
    for (const id of controllersRef.current.keys()) clearTaskTimers(id);
  }, [clearTaskTimers]);

  const hasPendingPrompts = tasks.some((task) => task.status === "pending");
  useEffect(() => {
    if (!hasPendingPrompts) return;

    const confirmClose = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const cancelOnClose = () => {
      for (const controller of controllersRef.current.values()) controller.abort();
    };

    window.addEventListener("beforeunload", confirmClose);
    window.addEventListener("pagehide", cancelOnClose);
    return () => {
      window.removeEventListener("beforeunload", confirmClose);
      window.removeEventListener("pagehide", cancelOnClose);
    };
  }, [hasPendingPrompts]);

  const unreadTasks = useMemo(
    () => tasks.filter((task) => task.status === "completed" && task.unread),
    [tasks],
  );

  const value = useMemo<IssaPromptContextValue>(() => ({
    tasks,
    unreadTasks,
    startPrompt,
    cancelPrompt,
    notifyWhenReady,
    removeTask,
    markRead,
  }), [cancelPrompt, markRead, notifyWhenReady, removeTask, startPrompt, tasks, unreadTasks]);

  return <IssaPromptContext.Provider value={value}>{children}</IssaPromptContext.Provider>;
}

export function useIssaPrompts(): IssaPromptContextValue {
  const context = useContext(IssaPromptContext);
  if (!context) throw new Error("useIssaPrompts must be used within IssaPromptProvider");
  return context;
}